import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  lcReviewNoteApi,
  type CancelReviewNoteRequest,
  type CreateReviewNoteRequest,
  type GradeReviewNoteItemRequest,
  type ReviewNoteScopeView,
  type ReviewNoteView,
  type UpdateReviewNoteItemsRequest,
} from '@/api/lcReviewNote'
import { coachingKeys } from './useCoachingToday'
import { normalizeSourceLessonIds } from '@/utils/reviewNoteSources'
import { gradingDraftScope, useClearGradingDraftRow } from './useGradingDraft'

export const reviewNoteSourceKeys = {
  previews: (learnerId: number) => ['coaching', 'learner', learnerId, 'review-note-preview'] as const,
  lessonLists: (learnerId: number) => ['coaching', 'learner', learnerId, 'review-note-source-lessons'] as const,
  lessons: (learnerId: number, lessonId: number, learningAttemptId: number | null) =>
    [...reviewNoteSourceKeys.lessonLists(learnerId), lessonId, learningAttemptId ?? 'progress'] as const,
  preview: (learnerId: number, lessonId: number, learningAttemptId: number | null, sourceLessonIds: number[]) =>
    [...reviewNoteSourceKeys.previews(learnerId), lessonId, learningAttemptId ?? 'progress', normalizeSourceLessonIds(sourceLessonIds)] as const,
}

export async function invalidateReviewNoteSources(queryClient: QueryClient, learnerId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: reviewNoteSourceKeys.lessonLists(learnerId) }),
    queryClient.invalidateQueries({ queryKey: reviewNoteSourceKeys.previews(learnerId) }),
  ])
}

export function useReviewNoteSourceLessons(
  learnerId: number,
  lessonId: number,
  learningAttemptId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: reviewNoteSourceKeys.lessons(learnerId, lessonId, learningAttemptId),
    queryFn: () => lcReviewNoteApi.getSourceLessons(learnerId, lessonId, learningAttemptId),
    enabled: enabled && learnerId > 0 && lessonId > 0,
    staleTime: 30_000,
  })
}

export function useReviewNotePreview(
  learnerId: number,
  lessonId: number,
  learningAttemptId: number | null,
  sourceLessonIds: number[],
  enabled = true,
) {
  const normalizedIds = normalizeSourceLessonIds(sourceLessonIds)
  return useQuery({
    queryKey: reviewNoteSourceKeys.preview(learnerId, lessonId, learningAttemptId, normalizedIds),
    queryFn: () => lcReviewNoteApi.preview(learnerId, lessonId, {
      learningAttemptId,
      sourceLessonIds: normalizedIds,
    }),
    enabled: enabled && learnerId > 0 && lessonId > 0 && normalizedIds.length > 0,
    // Always confirm counts from this range again when it is selected. Never carry
    // a previous range's counts into a new selection or a confirmation dialog.
    staleTime: 0,
  })
}

export function useReviewNote(
  learnerId: number,
  lessonId: number | null,
  learningAttemptId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: coachingKeys.reviewNote(learnerId, lessonId ?? 0, learningAttemptId),
    queryFn: () => lcReviewNoteApi.getScopeSummary(learnerId, lessonId as number, learningAttemptId),
    enabled: enabled && learnerId > 0 && lessonId != null && lessonId > 0,
    staleTime: 10_000,
  })
}

export function useReviewNoteSourceAvailability(
  learnerId: number,
  lessonId: number | null,
  learningAttemptId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: coachingKeys.reviewNoteSourceAvailability(
      learnerId,
      lessonId ?? 0,
      learningAttemptId,
    ),
    queryFn: () => lcReviewNoteApi.getSourceAvailability(
      learnerId,
      lessonId as number,
      learningAttemptId,
    ),
    enabled: enabled && learnerId > 0 && lessonId != null && lessonId > 0,
    staleTime: 10_000,
  })
}

export function useReviewNoteScope(
  learnerId: number,
  lessonId: number | null,
  learningAttemptId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: coachingKeys.reviewNoteScope(learnerId, lessonId ?? 0, learningAttemptId),
    queryFn: () => lcReviewNoteApi.getScope(learnerId, lessonId as number, learningAttemptId),
    enabled: enabled && learnerId > 0 && lessonId != null && lessonId > 0,
    staleTime: 10_000,
  })
}

export function useCreateReviewNote(learnerId: number, lessonId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateReviewNoteRequest) =>
      lcReviewNoteApi.create(learnerId, lessonId, request),
    onSuccess: (reviewNote) => {
      // Creation returns a summary without question snapshots or correct answers.
      writeReviewNoteCaches(queryClient, learnerId, reviewNote, false)
      return queryClient.invalidateQueries({
        queryKey: coachingKeys.reviewNoteDetail(learnerId, reviewNote.setId),
      })
    },
  })
}

async function invalidateCompletionState(
  queryClient: QueryClient,
  learnerId: number,
  lessonId: number,
  setId: number,
  learningAttemptId: number | null,
) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: coachingKeys.reviewNote(learnerId, lessonId, learningAttemptId),
    }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNoteDetail(learnerId, setId) }),
    queryClient.invalidateQueries({
      queryKey: coachingKeys.reviewNoteScope(learnerId, lessonId, learningAttemptId),
    }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.today() }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.lessonDetail(learnerId, lessonId) }),
    queryClient.invalidateQueries({
      queryKey: coachingKeys.guide(learnerId, lessonId, learningAttemptId),
    }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.workbookGrades(learnerId, lessonId) }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.designatedReviews(learnerId) }),
  ])
}

function writeReviewNoteCaches(
  queryClient: QueryClient,
  learnerId: number,
  reviewNote: ReviewNoteView,
  includeDetail = true,
) {
  queryClient.setQueryData(
    coachingKeys.reviewNote(learnerId, reviewNote.lessonId, reviewNote.learningAttemptId),
    reviewNote,
  )
  if (includeDetail && reviewNote.setId > 0) {
    queryClient.setQueryData(
      coachingKeys.reviewNoteDetail(learnerId, reviewNote.setId),
      reviewNote,
    )
  }
  queryClient.setQueryData<ReviewNoteScopeView>(
    coachingKeys.reviewNoteScope(learnerId, reviewNote.lessonId, reviewNote.learningAttemptId),
    (scope) => {
      if (scope == null || !Array.isArray(scope.sets)) return scope
      const existingIndex = scope.sets.findIndex((set) => set.setId === reviewNote.setId)
      if (existingIndex < 0) {
        return { ...scope, sets: [...scope.sets, reviewNote] }
      }
      return {
        ...scope,
        sets: scope.sets.map((set, index) => index === existingIndex ? reviewNote : set),
      }
    },
  )
}

export function useReviewNoteDetail(learnerId: number, setId: number | null, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.reviewNoteDetail(learnerId, setId ?? 0),
    queryFn: () => lcReviewNoteApi.getDetail(learnerId, setId as number),
    enabled: enabled && learnerId > 0 && setId != null && setId > 0,
    staleTime: 10_000,
  })
}

export function useReviewNotePool(learnerId: number, setId: number | null, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.reviewNotePool(learnerId, setId ?? 0),
    queryFn: () => lcReviewNoteApi.getPool(learnerId, setId as number),
    enabled: enabled && learnerId > 0 && setId != null && setId > 0,
    staleTime: 30_000,
  })
}

export function useUpdateReviewNoteItems(
  learnerId: number,
  setId: number,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdateReviewNoteItemsRequest) =>
      lcReviewNoteApi.updateItems(learnerId, setId, request),
    onSuccess: (reviewNote) => {
      writeReviewNoteCaches(queryClient, learnerId, reviewNote)
      return queryClient.invalidateQueries({
        queryKey: coachingKeys.reviewNotePool(learnerId, setId),
      })
    },
  })
}

export function useExcludeReviewNoteItem(learnerId: number) {
  const queryClient = useQueryClient()
  const clearDraftRow = useClearGradingDraftRow()
  return useMutation({
    mutationKey: ['coaching', 'learner', learnerId, 'exclude-review-note-item'],
    mutationFn: ({ setId, itemId }: { setId: number; itemId: number; quizId: string }) =>
      lcReviewNoteApi.updateItems(learnerId, setId, { removeItemIds: [itemId] }),
    onMutate: async ({ setId }) => {
      const detailKey = coachingKeys.reviewNoteDetail(learnerId, setId)
      const owner = queryClient.getQueryData<ReviewNoteView>(detailKey)
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        ...(owner == null ? [] : [
          queryClient.cancelQueries({ queryKey: coachingKeys.reviewNote(learnerId, owner.lessonId, owner.learningAttemptId) }),
          queryClient.cancelQueries({ queryKey: coachingKeys.reviewNoteScope(learnerId, owner.lessonId, owner.learningAttemptId) }),
        ]),
      ])
      return { owner, learnerId, clearDraftRow }
    },
    onSuccess: async (reviewNote, variables, context) => {
      const ownerLearnerId = context?.learnerId ?? learnerId
      const clearOwnerDraftRow = context?.clearDraftRow ?? clearDraftRow
      clearOwnerDraftRow(gradingDraftScope.reviewNote(variables.setId), variables.quizId)
      writeReviewNoteCaches(queryClient, ownerLearnerId, reviewNote)
      await Promise.all([
        invalidateCompletionState(queryClient, ownerLearnerId, reviewNote.lessonId, reviewNote.setId, reviewNote.learningAttemptId),
        queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNotePool(ownerLearnerId, reviewNote.setId) }),
        queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNotePrintQuizIds(ownerLearnerId, reviewNote.setId) }),
      ])
    },
    onError: async (_error, { setId }, context) => {
      // A concurrent print or grade may have locked this set. Re-read permissions
      // without removing the question locally when the save was rejected.
      const owner = context?.owner
      const ownerLearnerId = context?.learnerId ?? learnerId
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNoteDetail(ownerLearnerId, setId) }),
        ...(owner == null ? [] : [
          queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNote(ownerLearnerId, owner.lessonId, owner.learningAttemptId) }),
          queryClient.invalidateQueries({ queryKey: coachingKeys.reviewNoteScope(ownerLearnerId, owner.lessonId, owner.learningAttemptId) }),
        ]),
      ])
    },
  })
}

export function useGradeReviewNoteItem(learnerId: number, setId: number) {
  const queryClient = useQueryClient()
  const clearDraftRow = useClearGradingDraftRow()
  return useMutation({
    mutationFn: ({ quizId, ...request }: GradeReviewNoteItemRequest & { quizId: string }) =>
      lcReviewNoteApi.gradeItem(learnerId, setId, quizId, request),
    onSuccess: (reviewNote, variables) => {
      if (variables.answer != null) clearDraftRow(gradingDraftScope.reviewNote(setId), variables.quizId)
      writeReviewNoteCaches(queryClient, learnerId, reviewNote)
      return queryClient.invalidateQueries({ queryKey: reviewNoteSourceKeys.previews(learnerId) })
    },
  })
}

export function useCompleteReviewNote(
  learnerId: number,
  lessonId: number,
  setId: number,
  learningAttemptId: number | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => lcReviewNoteApi.complete(learnerId, setId),
    onSuccess: (reviewNote) => {
      writeReviewNoteCaches(queryClient, learnerId, reviewNote)
      return invalidateCompletionState(
        queryClient,
        learnerId,
        lessonId,
        setId,
        learningAttemptId,
      )
    },
  })
}

export function useCancelReviewNote(
  learnerId: number,
  lessonId: number,
  setId: number,
  learningAttemptId: number | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CancelReviewNoteRequest) =>
      lcReviewNoteApi.cancel(learnerId, setId, request),
    onSuccess: (reviewNote) => {
      writeReviewNoteCaches(queryClient, learnerId, reviewNote)
      return invalidateCompletionState(
        queryClient,
        learnerId,
        lessonId,
        setId,
        learningAttemptId,
      )
    },
  })
}

export function useLockReviewNoteForPrint(learnerId: number, setId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => lcReviewNoteApi.lockForPrint(learnerId, setId),
    onSuccess: (reviewNote) => {
      writeReviewNoteCaches(queryClient, learnerId, reviewNote)
      return queryClient.invalidateQueries({
        queryKey: coachingKeys.reviewNotePool(learnerId, setId),
      })
    },
  })
}

export function useReviewNotePrintQuizIds(
  learnerId: number,
  setId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: coachingKeys.reviewNotePrintQuizIds(learnerId, setId ?? 0),
    queryFn: () => lcReviewNoteApi.getPrintQuizIds(learnerId, setId as number),
    enabled: enabled && learnerId > 0 && setId != null && setId > 0,
    staleTime: Infinity,
  })
}
